import { useEffect, useState, useRef } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ClipboardList,
  Package,
  ReceiptText,
  Users,
  BellRing,
  LogOut,
  Menu,
  X,
  CalendarDays,
  Globe,
  DatabaseBackup,
  Bell,
  ShoppingCart,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useLang, LangToggle } from "@/lib/i18n";
import api from "@/lib/api";

const NAV = [
  {
    to: "/admin",
    end: true,
    icon: LayoutDashboard,
    labelKey: "dashboard",
    testid: "nav-admin-dashboard",
  },
  {
    to: "/admin/orders",
    icon: ClipboardList,
    labelKey: "orders",
    testid: "nav-admin-orders",
  },
  {
    to: "/admin/billing",
    icon: ReceiptText,
    labelKey: "billing",
    testid: "nav-admin-billing",
  },
  {
    to: "/admin/annual-returns",
    icon: ClipboardList,
    labelKey: "annualReturns",
    testid: "nav-admin-annual-returns",
  },
  {
    to: "/admin/catalog",
    icon: Package,
    labelKey: "productsStock",
    testid: "nav-admin-catalog",
  },
  {
    to: "/admin/customers",
    icon: Users,
    labelKey: "customers",
    testid: "nav-admin-customers",
  },
  {
    to: "/admin/reports",
    icon: CalendarDays,
    labelKey: "dailyRecords",
    testid: "nav-admin-reports",
  },
  {
    to: "/admin/settings",
    icon: BellRing,
    labelKey: "notifSettings",
    testid: "nav-admin-settings",
  },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [notificationIndex, setNotificationIndex] = useState(0);
  const adminSoundRef = useRef(null);
  const adminAudioContextRef = useRef(null);

  const stopNotificationSound = () => {
    const sound = adminSoundRef.current;
    if (!sound) return;

    sound.stopped = true;

    if (sound.timer) {
      clearTimeout(sound.timer);
    }

    adminSoundRef.current = null;
  };

  useEffect(() => {
    let active = true;

    const unlockAdminAudio = async () => {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;

      if (!adminAudioContextRef.current) {
        adminAudioContextRef.current = new AudioCtx();
      }

      const ctx = adminAudioContextRef.current;

      if (ctx.state === "suspended") {
        await ctx.resume();
      }
    };

    window.addEventListener("pointerdown", unlockAdminAudio);
    window.addEventListener("touchstart", unlockAdminAudio);
    window.addEventListener("keydown", unlockAdminAudio);

    const playNotificationSound = async () => {
      stopNotificationSound();

      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;

      if (!adminAudioContextRef.current) {
        adminAudioContextRef.current = new AudioCtx();
      }

      const ctx = adminAudioContextRef.current;

      if (ctx.state === "suspended") {
        await ctx.resume();
      }

      const sound = {
        ctx,
        stopped: false,
        timer: null,
      };

      adminSoundRef.current = sound;

      const master = ctx.createGain();
      master.gain.setValueAtTime(0.0001, ctx.currentTime);
      master.gain.exponentialRampToValueAtTime(0.58, ctx.currentTime + 0.03);
      master.connect(ctx.destination);

      const tone = (frequency, start, duration, volume = 0.72) => {
        if (sound.stopped) return;

        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = "sine";
        osc.frequency.setValueAtTime(frequency, start);

        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(volume, start + 0.025);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);

        osc.connect(gain);
        gain.connect(master);

        osc.start(start);
        osc.stop(start + duration);
      };

      const playMotif = () => {
        if (sound.stopped) return;

        const t = ctx.currentTime;

        tone(659, t, 0.28, 0.72);
        tone(784, t + 0.18, 0.28, 0.78);
        tone(988, t + 0.36, 0.38, 0.84);
        tone(1175, t + 0.62, 0.34, 0.8);
        tone(988, t + 0.92, 0.42, 0.7);
        tone(1319, t + 1.24, 0.62, 0.86);

        if (navigator.vibrate) {
          navigator.vibrate([100, 50, 100, 50, 160]);
        }

        sound.timer = setTimeout(() => {
          if (!sound.stopped) {
            playMotif();
          }
        }, 2250);
      };

      playMotif();
    };

    const addNotification = (notification, sound = true) => {
      if (!active || !notification) return;
      if (notification.mode !== "in_app") return;

      if (notification.kind === "order_claimed") {
        stopNotificationSound();
        const claimedOrderId = notification.order_id;
        setAdminNotifications((current) =>
          current.filter((n) => n.order_id !== claimedOrderId),
        );
        setNotificationIndex(0);
        return;
      }

      if (notification.kind !== "order_placed") return;

      setAdminNotifications((current) => {
        const id = notification.id || notification._id;
        if (id && current.some((n) => (n.id || n._id) === id)) return current;
        return [notification, ...current];
      });

      if (sound) playNotificationSound();
    };

    const loadNotifications = async () => {
      const res = await api.get("/admin/notifications");
      if (!active) return;
      (res.data || []).forEach((notification) =>
        addNotification(notification, false),
      );
    };

    loadNotifications();

    const token = localStorage.getItem("sf_token");
    let cleanupRealtime = () => {};

    if (token) {
      import("@/lib/realtimeNotifications")
        .then(({ connectRealtimeNotifications }) => {
          if (!active) return;
          cleanupRealtime = connectRealtimeNotifications({
            token,
            onNotification: (notification) =>
              addNotification(notification, true),
          });
        })
        .catch(() => {});
    }

    return () => {
      active = false;
      cleanupRealtime();
      window.removeEventListener("pointerdown", unlockAdminAudio);
      window.removeEventListener("touchstart", unlockAdminAudio);
      window.removeEventListener("keydown", unlockAdminAudio);
    };
  }, []);

  const currentAdminNotification =
    adminNotifications[notificationIndex] || null;

  const closeAdminNotification = async () => {
    if (!currentAdminNotification) return;

    stopNotificationSound();

    await api.post(
      `/admin/notifications/${currentAdminNotification.id || currentAdminNotification._id}/read`,
    );

    const nextIndex = notificationIndex + 1;

    if (nextIndex < adminNotifications.length) {
      setNotificationIndex(nextIndex);
    } else {
      setAdminNotifications([]);
      setNotificationIndex(0);
    }
  };

  const openAdminOrder = async () => {
    if (!currentAdminNotification) return;

    stopNotificationSound();

    await api.post(
      `/admin/notifications/${currentAdminNotification.id || currentAdminNotification._id}/read`,
    );

    const orderId = currentAdminNotification.order_id;
    setAdminNotifications([]);
    setNotificationIndex(0);

    if (orderId) {
      navigate(`/admin/orders?open=${orderId}`);
    } else {
      navigate("/admin/orders");
    }
  };
  const nav = user?.admin_type === "stock_admin"
    ? [
        {
          to: "/admin/stock",
          icon: Package,
          labelKey: "productsStock",
          testid: "nav-admin-stock",
        },
      ]
    : [
        ...NAV,
        ...(user?.is_master
          ? [
          {
            to: "/admin/business-content",
            icon: Globe,
            labelKey: "bizContentTitle",
            testid: "nav-admin-business",
          },
          {
            to: "/admin/backup",
            icon: DatabaseBackup,
            labelKey: "backupNav",
            testid: "nav-admin-backup",
          },
          {
            to: "/admin/stock-audit",
            icon: ClipboardList,
            labelKey: "stockAudit",
            testid: "nav-admin-stock-audit",
          },
            ]
          : []),
      ];


  const go = (item) => {
    navigate(item.to);
    setOpen(false);
  };

  const sidebar = (
    <div className="w-64 bg-[#0B192C] text-slate-300 flex flex-col h-full">
      <div className="px-5 py-5 border-b border-slate-800">
        <div className="font-display font-extrabold tracking-tight text-white">
          SABUJ SONA FERTILIZER
        </div>
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mt-0.5">
          {t("adminConsole")}
        </div>
      </div>
      <nav className="flex-1 py-4 px-3 space-y-1">
        {nav.map((item) => (
          <button
            key={item.to}
            data-testid={item.testid}
            onClick={() => go(item)}
            className={`w-full flex items-center gap-3 rounded-lg px-3.5 py-2.5 text-sm font-medium transition-colors ${
              (
                item.end
                  ? location.pathname === item.to
                  : location.pathname.startsWith(item.to)
              )
                ? "bg-slate-700 text-white"
                : "text-slate-300 hover:bg-slate-800 hover:text-white"
            }`}
          >
            <item.icon className="h-4 w-4" /> {t(item.labelKey)}
          </button>
        ))}
      </nav>
      <div className="p-4 border-t border-slate-800">
        <div className="text-sm font-semibold text-white">
          {user?.name || "Admin"}
        </div>
        <div className="text-xs text-slate-400 mb-3">+91 {user?.mobile}</div>
        <LangToggle className="mb-3" />
        <button
          data-testid="admin-logout-btn"
          onClick={logout}
          className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors"
        >
          <LogOut className="h-4 w-4" /> Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside className="hidden md:block shrink-0 sticky top-0 h-screen">
        {sidebar}
      </aside>
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-[#0B192C] h-14 flex items-center justify-between px-4">
        <div className="font-display font-bold text-white text-sm">
          SABUJ SONA · {t("adminConsole")}
        </div>
        <div className="flex items-center gap-2">
          <LangToggle />
          <button
            data-testid="admin-mobile-menu-btn"
            onClick={() => setOpen(!open)}
            className="text-white p-2"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden fixed inset-0 z-30 pt-14">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpen(false)}
          />
          <div className="relative h-full">{sidebar}</div>
        </div>
      )}
      {currentAdminNotification && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 p-4"
          data-testid="admin-order-notification-alert"
        >
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-emerald-200 overflow-hidden">
            <div className="bg-emerald-50 px-5 py-4 border-b border-emerald-200 flex items-start gap-3">
              <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                <Bell className="h-5 w-5 text-emerald-700" />
              </div>
              <div>
                <h2 className="font-display text-lg font-bold text-slate-900">
                  New Order Received
                </h2>
                <p className="text-sm text-slate-600 mt-0.5">
                  A dealer has placed a new order.
                </p>
              </div>
            </div>

            <div className="p-5">
              <div className="text-sm text-slate-500">
                Order{" "}
                <span className="font-semibold text-slate-900">
                  {currentAdminNotification.order_number ||
                    currentAdminNotification.order_id ||
                    "New Order"}
                </span>
              </div>

              <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center gap-2 font-semibold text-slate-900">
                  <ShoppingCart className="h-4 w-4 text-emerald-700" />
                  Dealer Details
                </div>

                <div className="mt-3 space-y-1 text-sm">
                  <div>
                    <span className="text-slate-500">Name: </span>
                    <span className="font-semibold text-slate-900">
                      {currentAdminNotification.customer_name || "—"}
                    </span>
                  </div>

                  <div>
                    <span className="text-slate-500">Mobile: </span>
                    <span className="font-semibold text-slate-900">
                      {currentAdminNotification.customer_mobile || "—"}
                    </span>
                  </div>

                  <div>
                    <span className="text-slate-500">Licence: </span>
                    <span className="font-semibold text-slate-900">
                      {currentAdminNotification.license_number || "—"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <div className="text-sm font-semibold text-slate-900 mb-2">
                  Ordered Products
                </div>

                <div className="space-y-2">
                  {(currentAdminNotification.items || []).map((item, i) => (
                    <div
                      key={item.id || i}
                      className="rounded-lg border border-slate-200 p-3 text-sm"
                    >
                      <div className="font-semibold text-slate-900">
                        {item.product_name || "Product"}
                      </div>
                      <div className="text-slate-600 mt-0.5">
                        Requested: {item.requested_qty}{" "}
                        {item.requested_unit || ""}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4 text-sm text-slate-500">
                {currentAdminNotification.message}
              </div>

              <div className="mt-5 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={closeAdminNotification}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  data-testid="admin-notification-dismiss"
                >
                  Dismiss
                </button>

                <button
                  type="button"
                  onClick={openAdminOrder}
                  className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800"
                  data-testid="admin-notification-view-order"
                >
                  View Order
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 pt-20 md:pt-6 lg:pt-8">
        <Outlet />
      </main>
    </div>
  );
}
